// Hooks for report grid rows. Mirrors use-dashboard-grid-rows.js against the
// parallel `/report_grid_rows` resource.
import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'

const getReportGridRows = async (token, reportId) => {
  const url = `reports/${reportId}/report_grid_rows`
  const data = await apiGet({ url, token })
  return data
}

export function useReportGridRows({ reportId, options = {} }) {
  const token = useContext(AuthContext)
  const queryFn = () => getReportGridRows(token, reportId)
  const queryKey = [`reports/${reportId}/report_grid_rows`]
  return useQuery(queryKey, queryFn, { enabled: !!reportId, ...options })
}

const createReportGridRow = async (token, reportId, params) => {
  const url = `reports/${reportId}/report_grid_rows`
  const objectName = 'report_grid_row'
  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateReportGridRow({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation((params) => createReportGridRow(token, reportId, params), {
    ...(options || {}),
    onSuccess: (newGridRow) => {
      queryClient.setQueryData([`reports/${reportId}/report_grid_rows`], (oldData) =>
        oldData ? [...oldData, newGridRow] : [newGridRow]
      )
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
      options?.onSuccess?.(newGridRow)
    }
  })
}

const updateReportGridRow = async (token, id, params) => {
  const url = `report_grid_rows/${id}`
  const objectName = 'report_grid_row'
  const data = await apiPatch({ url, token, objectName, params })
  return data
}

const destroyReportGridRow = async (token, id) => {
  const url = `report_grid_rows/${id}`
  await apiDelete({ url, token })
}

export function useDestroyReportGridRow({ reportId, id, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(() => destroyReportGridRow(token, id), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
      options?.onSuccess?.(...args)
    }
  })
}

export function useDestroyReportGridRowById({ reportId, options } = {}) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation((id) => destroyReportGridRow(token, id), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
      options?.onSuccess?.(...args)
    }
  })
}

export function useMoveReportGridRow({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(({ id, new_position }) => updateReportGridRow(token, id, { new_position }), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
      options?.onSuccess?.(...args)
    }
  })
}

// General-purpose row update (e.g. changing column_count). Unlike useMoveReportGridRow,
// which is hardcoded to send `new_position`, this PATCHes whatever permitted params it's given.
export function useUpdateReportGridRow({ reportId, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  return useMutation(({ id, ...params }) => updateReportGridRow(token, id, params), {
    ...(options || {}),
    onSuccess: (...args) => {
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
      options?.onSuccess?.(...args)
    }
  })
}
